import axios from 'axios';
import config from '../Config';
import authHeader from './auth-header';
import { handle401Unauthorized } from '../utils/Helper';

const API_URL = config.API_URL;
const API_VERSION = config.API_VERSION;

// Create a separate axios instance for CustomerType module to handle 401 errors
const customerTypeAxiosInstance = axios.create();

// Add response interceptor to handle 401 errors for CustomerType module
customerTypeAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      handle401Unauthorized();
    }
    return Promise.reject(error);
  }
);

// CustomerType APIs
const getCustomerTypeList = (page, pagesize, search, ordering, status) => {
  return customerTypeAxiosInstance.get(API_URL + '/api/' + API_VERSION + '/customer-types/', {
    headers: authHeader(),
    params: {
      deleted: 0,
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
      ...(status && { status: status }),
    },
  });
};

const getCustomerTypeById = id => {
  return customerTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/customer-types/${id}/`, {
    headers: authHeader(),
  });
};

const addCustomerType = formData => {
  return customerTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/customer-types/`,
    formData,
    { headers: authHeader() },
  );
};

const updateCustomerType = (id, formPayload) => {
  return customerTypeAxiosInstance.put(
    `${API_URL}/api/${API_VERSION}/customer-types/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const patchCustomerType = (id, formPayload) => {
  return customerTypeAxiosInstance.patch(
    `${API_URL}/api/${API_VERSION}/customer-types/${id}/`,
    formPayload,
    { headers: authHeader() },
  );
};

const changeCustomerTypeStatus = (id, status) => {
  return customerTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/customer-types/${id}/change-status/`,
    { status: status },
    { headers: authHeader() },
  );
};

const deleteCustomerType = id => {
  return customerTypeAxiosInstance.delete(
    `${API_URL}/api/${API_VERSION}/customer-types/${id}/`,
    { headers: authHeader() },
  );
};

const getCustomerTypeDropdown = (page, pagesize, search, ordering) => {
  return customerTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/customer-types/dropdown/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getCustomerTypeArchiveList = (page, pagesize, search, ordering) => {
  return customerTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/customer-types-archive/`, {
    headers: authHeader(),
    params: {
      page: page,
      pagesize: pagesize,
      ...(search && { search: search }),
      ...(ordering && { ordering: ordering }),
    },
  });
};

const getCustomerTypeArchiveById = id => {
  return customerTypeAxiosInstance.get(`${API_URL}/api/${API_VERSION}/customer-types-archive/${id}/`, {
    headers: authHeader(),
  });
};

const restoreCustomerType = ids => {
  return customerTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/customer-types/bulk-restore/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

const bulkArchiveCustomerType = ids => {
  return customerTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/customer-types/bulk-archive/`,
    { ids: Array.isArray(ids) ? ids : [ids] },
    { headers: authHeader() },
  );
};

// Bulk Import APIs
const bulkImportCustomerTypes = formData => {
  return customerTypeAxiosInstance.post(
    `${API_URL}/api/${API_VERSION}/customer-types/bulk-import/`,
    formData,
    {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

const getImportLogs = (page, pagesize) => {
  return customerTypeAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/customer-types/import-logs/`,
    {
      headers: authHeader(),
      params: {
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const getImportErrors = (importLogId, page, pagesize) => {
  return customerTypeAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/customer-types/import-errors/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
        page: page,
        pagesize: pagesize,
      },
    },
  );
};

const downloadErrorReport = importLogId => {
  return customerTypeAxiosInstance.get(
    `${API_URL}/api/${API_VERSION}/customer-types/error-report/download/`,
    {
      headers: authHeader(),
      params: {
        import_log: importLogId,
      },
      responseType: 'blob',
    },
  );
};

const customerTypeService = {
  getCustomerTypeList,
  getCustomerTypeById,
  addCustomerType,
  updateCustomerType,
  patchCustomerType,
  changeCustomerTypeStatus,
  deleteCustomerType,
  getCustomerTypeDropdown,
  getCustomerTypeArchiveList,
  getCustomerTypeArchiveById,
  restoreCustomerType,
  bulkArchiveCustomerType,
  bulkImportCustomerTypes,
  getImportLogs,
  getImportErrors,
  downloadErrorReport,
};

export default customerTypeService;
